import { createSvgEl } from '@learnforge/demo-render';

// ── Layout constants ──────────────────────────────────────────────────────────

const CELL_W = 70;
const CELL_H = 50;
const PAD_X = 10;
const PAD_Y = 10;

// ── Public API ────────────────────────────────────────────────────────────────

export interface PairGridOptions {
  rows: readonly string[];
  cols: readonly string[];
  highlight?: ReadonlySet<string>;
  diagonal?: boolean;
  onToggle?(key: string): void;
}

export interface PairGridHandle {
  group: SVGGElement;
  update(opts: Partial<PairGridOptions>): void;
  destroy(): void;
}

// ── createPairGrid ────────────────────────────────────────────────────────────

/**
 * Renders a rows × cols grid of pair cells as an SVG group.
 *
 * Each cell is `<g data-pair="row:col">` containing a text label `(row,col)`.
 * - `highlight` set adds `is-highlight` class to matching cells.
 * - `diagonal` adds `is-diagonal` to cells where row === col (for A² grids).
 * - `onToggle` fires with `"row:col"` on cell click.
 * - No inline colours.
 */
export function createPairGrid(
  parent: SVGSVGElement | SVGGElement,
  opts: PairGridOptions,
): PairGridHandle {
  let current: PairGridOptions = { ...opts };

  const group = createSvgEl('g', { class: 'pg-grid' });
  parent.appendChild(group);

  function render(): void {
    group.replaceChildren();

    const { rows, cols, highlight, diagonal } = current;

    for (let r = 0; r < rows.length; r++) {
      const row = rows[r] as string;
      for (let c = 0; c < cols.length; c++) {
        const col = cols[c] as string;
        const key = `${row}:${col}`;

        const x = PAD_X + c * CELL_W;
        const y = PAD_Y + r * CELL_H;

        let cls = 'pg-cell';
        if (highlight?.has(key)) {
          cls += ' is-highlight';
        }
        if (diagonal && row === col) {
          cls += ' is-diagonal';
        }

        const g = createSvgEl('g', {
          class: cls,
          'data-pair': key,
        });

        const rect = createSvgEl('rect', {
          class: 'pg-cell-bg',
          x,
          y,
          width: CELL_W - 2,
          height: CELL_H - 2,
          rx: 4,
        });

        const text = createSvgEl('text', {
          class: 'pg-cell-label',
          x: x + (CELL_W - 2) / 2,
          y: y + (CELL_H - 2) / 2,
          'text-anchor': 'middle',
          'dominant-baseline': 'central',
        });
        text.textContent = `(${row},${col})`;

        g.appendChild(rect);
        g.appendChild(text);

        if (current.onToggle) {
          g.addEventListener('click', () => current.onToggle?.(key));
        }

        group.appendChild(g);
      }
    }
  }

  render();

  return {
    group,

    update(partial: Partial<PairGridOptions>): void {
      current = { ...current, ...partial };
      render();
    },

    destroy(): void {
      group.remove();
    },
  };
}
